import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, User, Link as LinkIcon, Globe, Image, Star, Package, Calendar, Loader2, FileText, Mail, CheckCircle, Info, AlertCircle, KeyRound } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarInitials, getAvatarSource } from "@/lib/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";
import { EventOfferingsDialog } from "@/components/event-offerings-dialog";
import { BookingFormsDialog } from "@/components/booking-forms-dialog";
import type { Talent, User as UserType, UserProfile } from "@shared/schema";

interface GoogleCalendar {
  id: string;
  summary: string;
  primary?: boolean;
  accessRole: string;
}

interface UserWithProfile extends UserType {
  profile?: UserProfile;
}

interface ManageTalentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function ManageTalentDialog({ open, onOpenChange, inPanel = false }: ManageTalentDialogProps) {
  const { toast } = useToast();
  // Task #107 — Route every workspace-scoped mutation through `buildPath`
  // so the server resolves the target workspace from the URL prefix
  // instead of falling back to the session default. Bare `/api/talent`
  // writes silently leaked into the wrong tenant (e.g. warehouse-productions)
  // when an admin was operating in a different workspace.
  const { buildPath } = useWorkspace();
  const [editingTalent, setEditingTalent] = useState<Talent | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [eventOfferingsOpen, setEventOfferingsOpen] = useState(false);
  const [selectedTalentForOfferings, setSelectedTalentForOfferings] = useState<Talent | null>(null);
  const [showAddOfferingsPrompt, setShowAddOfferingsPrompt] = useState(false);
  const [newlyCreatedTalent, setNewlyCreatedTalent] = useState<Talent | null>(null);
  const [bookingFormsDefaultTab, setBookingFormsDefaultTab] = useState<string>("forms");
  const [bookingFormDialogOpen, setBookingFormDialogOpen] = useState(false);
  const [selectedTalentForBookingForm, setSelectedTalentForBookingForm] = useState<Talent | null>(null);
  const [connectCalDialogOpen, setConnectCalDialogOpen] = useState(false);
  const [selectedTalentForConnectCal, setSelectedTalentForConnectCal] = useState<Talent | null>(null);
  const [bookingFormData, setBookingFormData] = useState({
    bookingFormTitle: "",
    bookingFormSubtitle: "",
    bookingFormDisclaimer: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    photoUrl: "",
    bio: "",
    eventDescription: "",
    websiteUrl: "",
    socialMediaUrls: [""],
    googleDriveUrl: "",
    googleReviewUrl: "",
    calendarIds: [] as string[],
    userId: "",
    isActive: true,
    isDefault: false,
    // Task #611 — controls whether $ amounts are surfaced to the linked
    // artist on /api/schedule and tour responses. Default OFF.
    financialsVisibleToTalent: false,
    // Task #708 — per-talent calendar push field overrides. `null` =
    // inherit workspace default; `true`/`false` = explicit override.
    calPushTitleAccountName: null as boolean | null,
    calPushTitleAddress: null as boolean | null,
    calPushTitleRebookPrefix: null as boolean | null,
    calPushAddressInBody: null as boolean | null,
    calPushContactName: null as boolean | null,
    calPushContactPhone: null as boolean | null,
    calPushRate: null as boolean | null,
    calPushReviewLink: null as boolean | null,
    calPushHoldStatus: null as boolean | null,
    allowPostReviewClientEmail: null as boolean | null,
    // Talent Type — two-tier catalog (platform defaults + workspace custom)
    talentTypeId: "" as string,
    talentTypeSource: "" as string,
  });

  const { data: talent = [], isLoading } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });

  interface TalentTypeCatalog {
    platformTypes: { id: string; name: string; sortOrder: number; isActive: boolean }[];
    workspaceTypes: { id: string; name: string; sortOrder: number; isActive: boolean }[];
    hiddenPlatformTypeIds: string[];
  }

  const { data: talentTypeCatalog } = useQuery<TalentTypeCatalog>({
    queryKey: ["/api/talent-types"],
    enabled: open,
  });

  const effectiveTalentTypes = (() => {
    if (!talentTypeCatalog) return [];
    const hiddenSet = new Set(talentTypeCatalog.hiddenPlatformTypeIds);
    const platform = talentTypeCatalog.platformTypes
      .filter((t) => !hiddenSet.has(t.id))
      .map((t) => ({ id: t.id, name: t.name, source: "platform" as const }));
    const workspace = talentTypeCatalog.workspaceTypes
      .map((t) => ({ id: t.id, name: t.name, source: "workspace" as const }));
    return [...platform, ...workspace];
  })();

  const resolveTalentTypeName = (typeId: string | null | undefined, source: string | null | undefined): string | null => {
    if (!typeId || !source || !talentTypeCatalog) return null;
    if (source === "platform") {
      return talentTypeCatalog.platformTypes.find((t) => t.id === typeId)?.name ?? null;
    }
    return talentTypeCatalog.workspaceTypes.find((t) => t.id === typeId)?.name ?? null;
  };

  const { data: users = [] } = useQuery<UserWithProfile[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  // Use talent-specific calendar endpoint when editing (uses talent's OAuth tokens if connected)
  const calendarsQueryKey = editingTalent 
    ? ["/api/talent", editingTalent.id, "calendars"]
    : ["/api/calendar/list"];
  
  const { data: calendarsData, isLoading: calendarsLoading } = useQuery<{ calendars: GoogleCalendar[]; connected?: boolean; source?: string }>({
    queryKey: calendarsQueryKey,
    queryFn: async () => {
      const endpoint = editingTalent 
        ? `/api/talent/${editingTalent.id}/calendars`
        : "/api/calendar/list";
      const res = await fetch(endpoint, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load calendars");
      return res.json();
    },
    enabled: open && (isCreating || !!editingTalent),
  });

  const availableCalendars = calendarsData?.calendars || [];
  const hasTalentCalendar = calendarsData?.connected ?? false;

  const linkableUsers = users.filter(u => u.profile?.role === "artist" || u.profile?.role === "admin");

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const filteredUrls = data.socialMediaUrls.filter(url => url.trim() !== "");
      const response = await apiRequest("POST", buildPath("/api/talent"), {
        ...data,
        socialMediaUrls: filteredUrls.length > 0 ? filteredUrls : null,
        calendarIds: data.calendarIds.length > 0 ? data.calendarIds : null,
        userId: data.userId || null,
      });
      return response.json();
    },
    onSuccess: (createdTalent: Talent) => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Talent Created", description: "The new talent profile has been created." });
      resetForm();
      setNewlyCreatedTalent(createdTalent);
      setShowAddOfferingsPrompt(true);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create talent.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const filteredUrls = data.socialMediaUrls.filter(url => url.trim() !== "");
      const response = await apiRequest("PATCH", buildPath(`/api/talent/${id}`), {
        ...data,
        socialMediaUrls: filteredUrls.length > 0 ? filteredUrls : null,
        calendarIds: data.calendarIds.length > 0 ? data.calendarIds : null,
        userId: data.userId || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Talent Updated", description: "The talent profile has been updated." });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update talent.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", buildPath(`/api/talent/${id}`));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Talent Deleted", description: "The talent profile has been removed." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete talent.", variant: "destructive" });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", buildPath(`/api/talent/${id}/set-default`));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Default Talent Set", description: "This talent is now the default for new leads." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to set default talent.", variant: "destructive" });
    },
  });

  const clearDefaultMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", buildPath(`/api/talent/${id}/clear-default`));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Default Cleared", description: "This talent is no longer the default." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to clear default talent.", variant: "destructive" });
    },
  });

  const updateBookingFormMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof bookingFormData }) => {
      const response = await apiRequest("PATCH", buildPath(`/api/talent/${id}`), data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Booking Form Updated", description: "The booking form text has been saved." });
      setBookingFormDialogOpen(false);
      setSelectedTalentForBookingForm(null);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update booking form.", variant: "destructive" });
    },
  });

  const { data: connectCalStatus, isLoading: connectCalLoading } = useQuery<{
    calendars: GoogleCalendar[];
    connected?: boolean;
    source?: string;
    connectedEmail?: string;
  }>({
    queryKey: ["/api/talent", selectedTalentForConnectCal?.id, "calendars"],
    queryFn: async () => {
      const res = await fetch(`/api/talent/${selectedTalentForConnectCal!.id}/calendars`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: connectCalDialogOpen && !!selectedTalentForConnectCal?.id,
  });

  const sendCalInviteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath(`/api/talent/${selectedTalentForConnectCal!.id}/calendar/invite`));
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Invite Sent", description: `A calendar connection link has been emailed to ${selectedTalentForConnectCal?.name}.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to send invite.", variant: "destructive" });
    },
  });

  const disconnectCalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath(`/api/talent/${selectedTalentForConnectCal!.id}/calendar/disconnect`));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent", selectedTalentForConnectCal?.id, "calendars"] });
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Calendar Disconnected", description: `Google Calendar has been unlinked from ${selectedTalentForConnectCal?.name}.` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect calendar.", variant: "destructive" });
    },
  });

  const openBookingFormDialog = (talent: Talent) => {
    setSelectedTalentForBookingForm(talent);
    setBookingFormData({
      bookingFormTitle: talent.bookingFormTitle || "",
      bookingFormSubtitle: talent.bookingFormSubtitle || "",
      bookingFormDisclaimer: talent.bookingFormDisclaimer || "",
    });
    setBookingFormDialogOpen(true);
  };

  const handleSaveBookingForm = () => {
    if (selectedTalentForBookingForm) {
      updateBookingFormMutation.mutate({
        id: selectedTalentForBookingForm.id,
        data: bookingFormData,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      photoUrl: "",
      bio: "",
      eventDescription: "",
      websiteUrl: "",
      socialMediaUrls: [""],
      googleDriveUrl: "",
      googleReviewUrl: "",
      calendarIds: [],
      userId: "",
      isActive: true,
      isDefault: false,
      financialsVisibleToTalent: false,
      calPushTitleAccountName: null,
      calPushTitleAddress: null,
      calPushTitleRebookPrefix: null,
      calPushAddressInBody: null,
      calPushContactName: null,
      calPushContactPhone: null,
      calPushRate: null,
      calPushReviewLink: null,
      calPushHoldStatus: null,
      allowPostReviewClientEmail: null,
      talentTypeId: "",
      talentTypeSource: "",
    });
    setEditingTalent(null);
    setIsCreating(false);
  };

  const startEdit = (talent: Talent) => {
    setEditingTalent(talent);
    const urls = talent.socialMediaUrls && talent.socialMediaUrls.length > 0 
      ? talent.socialMediaUrls 
      : [""];
    setFormData({
      name: talent.name,
      email: talent.email || "",
      photoUrl: talent.photoUrl || "",
      bio: talent.bio || "",
      eventDescription: talent.eventDescription || "",
      websiteUrl: talent.websiteUrl || "",
      socialMediaUrls: urls,
      googleDriveUrl: talent.googleDriveUrl || "",
      googleReviewUrl: talent.googleReviewUrl || "",
      calendarIds: talent.calendarIds || [],
      userId: talent.userId || "",
      isActive: talent.isActive,
      isDefault: talent.isDefault,
      financialsVisibleToTalent: !!talent.financialsVisibleToTalent,
      calPushTitleAccountName: talent.calPushTitleAccountName ?? null,
      calPushTitleAddress: talent.calPushTitleAddress ?? null,
      calPushTitleRebookPrefix: talent.calPushTitleRebookPrefix ?? null,
      calPushAddressInBody: talent.calPushAddressInBody ?? null,
      calPushContactName: talent.calPushContactName ?? null,
      calPushContactPhone: talent.calPushContactPhone ?? null,
      calPushRate: talent.calPushRate ?? null,
      calPushReviewLink: talent.calPushReviewLink ?? null,
      calPushHoldStatus: talent.calPushHoldStatus ?? null,
      allowPostReviewClientEmail: talent.allowPostReviewClientEmail ?? null,
      talentTypeId: (talent as any).talentTypeId || "",
      talentTypeSource: (talent as any).talentTypeSource || "",
    });
    setIsCreating(false);
  };

  const toggleCalendar = (calendarId: string) => {
    setFormData(prev => ({
      ...prev,
      calendarIds: prev.calendarIds.includes(calendarId)
        ? prev.calendarIds.filter(id => id !== calendarId)
        : [...prev.calendarIds, calendarId]
    }));
  };

  const openEventOfferings = (talent: Talent) => {
    setSelectedTalentForOfferings(talent);
    setEventOfferingsOpen(true);
  };

  const handleAddOfferingsNow = () => {
    setShowAddOfferingsPrompt(false);
    if (newlyCreatedTalent) {
      openEventOfferings(newlyCreatedTalent);
    }
    setNewlyCreatedTalent(null);
  };

  const handleAddOfferingsLater = () => {
    setShowAddOfferingsPrompt(false);
    setNewlyCreatedTalent(null);
  };

  const addSocialMediaUrl = () => {
    setFormData(prev => ({
      ...prev,
      socialMediaUrls: [...prev.socialMediaUrls, ""]
    }));
  };

  const removeSocialMediaUrl = (index: number) => {
    setFormData(prev => ({
      ...prev,
      socialMediaUrls: prev.socialMediaUrls.filter((_, i) => i !== index)
    }));
  };

  const updateSocialMediaUrl = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      socialMediaUrls: prev.socialMediaUrls.map((url, i) => i === index ? value : url)
    }));
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Name is required.", variant: "destructive" });
      return;
    }
    
    if (editingTalent) {
      updateMutation.mutate({ id: editingTalent.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getLinkedUserName = (userId: string | null) => {
    if (!userId) return null;
    const user = users.find(u => u.id === userId);
    return user?.firstName && user?.lastName 
      ? `${user.firstName} ${user.lastName}`
      : user?.email || null;
  };

  // Child dialogs rendered separately so they are never unmounted by parent re-renders
  const childDialogs = (
    <>
      <AlertDialog open={showAddOfferingsPrompt} onOpenChange={setShowAddOfferingsPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Event Offerings</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to add show offerings for {newlyCreatedTalent?.name} now?
              You will not be able to book an event without them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleAddOfferingsLater}>Do this later</AlertDialogCancel>
            <AlertDialogAction onClick={handleAddOfferingsNow}>Yes, add now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EventOfferingsDialog
        open={eventOfferingsOpen}
        onOpenChange={setEventOfferingsOpen}
        talentId={selectedTalentForOfferings?.id}
        talentName={selectedTalentForOfferings?.name}
      />

      <BookingFormsDialog
        open={bookingFormDialogOpen}
        onOpenChange={(v) => {
          setBookingFormDialogOpen(v);
          if (!v) setBookingFormsDefaultTab("forms");
        }}
        talent={selectedTalentForBookingForm}
        defaultTab={bookingFormsDefaultTab}
      />

      {/* Connect Calendar dialog — dedicated connection-status + two-path flow */}
      <Dialog open={connectCalDialogOpen} onOpenChange={(v) => {
        setConnectCalDialogOpen(v);
        if (!v) setSelectedTalentForConnectCal(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Connect Calendar — {selectedTalentForConnectCal?.name}</DialogTitle>
            <DialogDescription>
              Link a Google Calendar directly to this talent so bookings are written to their calendar and availability checks use their schedule.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            {/* Connection status */}
            {connectCalLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : connectCalStatus?.connected ? (
              <div className="space-y-2">
                <Alert className="border-green-200 dark:border-green-800">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription>
                    <strong>Calendar connected</strong>
                    {connectCalStatus.connectedEmail && (
                      <span className="ml-1 text-muted-foreground">({connectCalStatus.connectedEmail})</span>
                    )}.
                    Bookings and availability checks use this calendar.
                  </AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-destructive border-destructive/40 hover:bg-destructive/10"
                  onClick={() => disconnectCalMutation.mutate()}
                  disabled={disconnectCalMutation.isPending}
                  data-testid="button-disconnect-talent-calendar"
                >
                  {disconnectCalMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disconnecting...</>
                  ) : (
                    <><LinkIcon className="w-4 h-4 mr-2" />Disconnect Calendar</>
                  )}
                </Button>
              </div>
            ) : (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  No Google Calendar is connected for {selectedTalentForConnectCal?.name}. Choose an option below to link one.
                </AlertDescription>
              </Alert>
            )}

            {/* Path A: Admin-initiated OAuth */}
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="w-4 h-4 text-blue-500" />
                {connectCalStatus?.connected ? "Reconnect via OAuth" : "Connect via OAuth"}
              </div>
              <p className="text-sm text-muted-foreground">
                {connectCalStatus?.connected
                  ? "Replace the connected Google account with a different one."
                  : "You'll be redirected to Google's consent screen to link a calendar directly to this talent."}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  if (selectedTalentForConnectCal) {
                    window.location.href = `/api/talent/${selectedTalentForConnectCal.id}/calendar/auth`;
                  }
                }}
                data-testid="button-admin-oauth-calendar-dialog"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Connect via Google OAuth
              </Button>
            </div>

            {/* Path B: Send invite to talent */}
            <div className="border rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="w-4 h-4 text-green-500" />
                Invite Talent to Self-Connect
              </div>
              <p className="text-sm text-muted-foreground">
                {selectedTalentForConnectCal?.email
                  ? `Send ${selectedTalentForConnectCal.name} (${selectedTalentForConnectCal.email}) a secure link to connect their own Google Calendar.`
                  : "Add an email address to this talent profile to send an invite."}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={!selectedTalentForConnectCal?.email || sendCalInviteMutation.isPending}
                onClick={() => sendCalInviteMutation.mutate()}
                data-testid="button-send-cal-invite-dialog"
              >
                {sendCalInviteMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" />Send Invite Email</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  const mainContent = (
    <>
      <DialogHeader>
        <DialogTitle>Manage Talent</DialogTitle>
        <DialogDescription>
          Configure talent profiles. Each talent can have their own shows, clients, and revenue tracking.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
          {!isCreating && !editingTalent && (
            <>
              <div className="flex justify-end">
                <Button onClick={startCreate} data-testid="button-add-talent">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Talent
                </Button>
              </div>

              {isLoading ? (
                <div className="py-8 text-center text-muted-foreground">Loading talent...</div>
              ) : talent.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">No talent configured yet.</div>
              ) : (
                <div className="space-y-3">
                  {talent.map((talent) => (
                    <Card key={talent.id} data-testid={`card-talent-${talent.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="w-12 h-12 flex-shrink-0">
                            <AvatarImage
                              src={getAvatarSource(talent)}
                              alt={talent.name}
                              className="object-cover"
                            />
                            <AvatarFallback className="bg-muted">
                              {getAvatarInitials(talent.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium" data-testid={`text-talent-name-${talent.id}`}>
                                {talent.name}
                              </span>
                              {talent.isDefault && (
                                <Badge variant="secondary" className="text-xs">Default</Badge>
                              )}
                              {!talent.isActive && (
                                <Badge variant="outline" className="text-xs text-muted-foreground">Inactive</Badge>
                              )}
                              {(() => {
                                const typeName = resolveTalentTypeName((talent as any).talentTypeId, (talent as any).talentTypeSource);
                                return typeName ? (
                                  <Badge variant="outline" className="text-xs" data-testid={`badge-talent-type-${talent.id}`}>
                                    {typeName}
                                  </Badge>
                                ) : null;
                              })()}
                            </div>
                            {talent.bio && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{talent.bio}</p>
                            )}
                            {talent.userId && (
                              <div className="flex items-center gap-1 mt-1">
                                <LinkIcon className="w-3 h-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  Linked to: {getLinkedUserName(talent.userId)}
                                </span>
                              </div>
                            )}
                            {talent.websiteUrl && (
                              <div className="flex items-center gap-1 mt-1">
                                <Globe className="w-3 h-3 text-muted-foreground" />
                                <a href={talent.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                                  {talent.websiteUrl}
                                </a>
                              </div>
                            )}
                            {talent.bookingSlug && (
                              <div className="flex items-center gap-1 mt-1">
                                <LinkIcon className="w-3 h-3 text-green-600" />
                                <span className="text-xs text-muted-foreground">
                                  Booking: <code className="bg-muted px-1 rounded">/book/{talent.bookingSlug}</code>
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-start gap-3">
                            <div className="flex gap-1 items-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (talent.isDefault) {
                                    clearDefaultMutation.mutate(talent.id);
                                  } else {
                                    setDefaultMutation.mutate(talent.id);
                                  }
                                }}
                                disabled={setDefaultMutation.isPending || clearDefaultMutation.isPending}
                                title={talent.isDefault ? "Remove as default talent" : "Set as default talent for new leads"}
                                data-testid={`button-set-default-talent-${talent.id}`}
                              >
                                <Star className={`w-4 h-4 ${talent.isDefault ? "fill-amber-500 text-amber-500" : "text-amber-500"}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => startEdit(talent)}
                                data-testid={`button-edit-talent-${talent.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {!talent.isDefault && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteMutation.mutate(talent.id)}
                                  data-testid={`button-delete-talent-${talent.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEventOfferings(talent)}
                                className="w-[170px] justify-start"
                                data-testid={`button-event-offerings-${talent.id}`}
                              >
                                <Package className="w-4 h-4 mr-2" />
                                Event Offerings
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTalentForConnectCal(talent);
                                  setConnectCalDialogOpen(true);
                                }}
                                className="w-[170px] justify-start"
                                data-testid={`button-connect-calendar-${talent.id}`}
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                Connect Calendar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTalentForBookingForm(talent);
                                  setBookingFormsDefaultTab("forms");
                                  setBookingFormDialogOpen(true);
                                }}
                                className="w-[170px] justify-start"
                                data-testid={`button-booking-forms-${talent.id}`}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                Booking Forms
                              </Button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {(isCreating || editingTalent) && (
            <div className="space-y-4 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{editingTalent ? "Edit Talent" : "New Talent"}</h3>
                <Button variant="ghost" size="sm" onClick={resetForm}>Cancel</Button>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Talent name"
                    data-testid="input-talent-name"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="talent@email.com"
                    data-testid="input-talent-email"
                  />
                  <p className="text-xs text-muted-foreground">Used for calendar link requests</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="photoUrl">Photo URL</Label>
                  <Input
                    id="photoUrl"
                    value={formData.photoUrl}
                    onChange={(e) => setFormData({ ...formData, photoUrl: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                    data-testid="input-talent-photo"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Brief biography..."
                    rows={3}
                    data-testid="input-talent-bio"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="eventDescription">Event Description</Label>
                  <Textarea
                    id="eventDescription"
                    value={formData.eventDescription}
                    onChange={(e) => setFormData({ ...formData, eventDescription: e.target.value })}
                    placeholder="Description of the event..."
                    rows={3}
                    data-testid="input-talent-show-description"
                  />
                </div>

                {/* Talent Type selector */}
                <div className="grid gap-2">
                  <Label htmlFor="talentType">Talent Type</Label>
                  <Select
                    value={formData.talentTypeId ? `${formData.talentTypeSource}:${formData.talentTypeId}` : "none"}
                    onValueChange={(value) => {
                      if (value === "none") {
                        setFormData({ ...formData, talentTypeId: "", talentTypeSource: "" });
                      } else {
                        const [source, ...idParts] = value.split(":");
                        setFormData({ ...formData, talentTypeId: idParts.join(":"), talentTypeSource: source });
                      }
                    }}
                  >
                    <SelectTrigger id="talentType" data-testid="select-talent-type">
                      <SelectValue placeholder="Select type (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No type set</SelectItem>
                      {effectiveTalentTypes.map((t) => (
                        <SelectItem key={`${t.source}:${t.id}`} value={`${t.source}:${t.id}`}>
                          {t.name}
                          {t.source === "workspace" && (
                            <span className="ml-1 text-xs text-muted-foreground">(custom)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Optional. Categorizes this act. Manage available types in Workspace Settings → Talent Types.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="userId">Link to User Account</Label>
                  <Select
                    value={formData.userId || "none"}
                    onValueChange={(value) => setFormData({ ...formData, userId: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="select-talent-user">
                      <SelectValue placeholder="Select a user (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No linked user</SelectItem>
                      {linkableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.firstName} {user.lastName} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Link to an artist-role user so they can manage their own profile
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="websiteUrl">Website</Label>
                    <Input
                      id="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      placeholder="https://"
                      data-testid="input-talent-website"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Social Media Links</Label>
                    <div className="space-y-2">
                      {formData.socialMediaUrls.map((url, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={url}
                            onChange={(e) => updateSocialMediaUrl(index, e.target.value)}
                            placeholder="https://facebook.com/... or Instagram/YouTube"
                            data-testid={`input-talent-social-${index}`}
                          />
                          {formData.socialMediaUrls.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeSocialMediaUrl(index)}
                              data-testid={`button-remove-social-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addSocialMediaUrl}
                        data-testid="button-add-social-link"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Link
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="googleDriveUrl">EPK Link</Label>
                  <Input
                    id="googleDriveUrl"
                    value={formData.googleDriveUrl}
                    onChange={(e) => setFormData({ ...formData, googleDriveUrl: e.target.value })}
                    placeholder="https://drive.google.com/drive/folders/..."
                    data-testid="input-talent-drive"
                  />
                  <p className="text-xs text-muted-foreground">Public/shareable link to EPK, media assets, or promotional materials. No Google Drive connection required.</p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="googleReviewUrl">Google Review Link</Label>
                  <Input
                    id="googleReviewUrl"
                    value={formData.googleReviewUrl}
                    onChange={(e) => setFormData({ ...formData, googleReviewUrl: e.target.value })}
                    placeholder="https://g.page/r/... or https://search.google.com/local/writereview?placeid=..."
                    data-testid="input-talent-review-url"
                  />
                  <p className="text-xs text-muted-foreground">
                    Paste your Google Maps review page URL. Use <code className="bg-muted px-1 rounded text-xs">{"{{review_link}}"}</code> in automation emails to send each client a tracked link that routes here.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Calendars for Availability
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which Google Calendars to check when showing available time slots on this talent's booking page.
                  </p>
                  {calendarsLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading calendars...
                    </div>
                  ) : availableCalendars.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No calendars available. Connect Google Calendar in the Admin Panel first.
                    </p>
                  ) : (
                    <div className="space-y-2 border rounded-md p-3 max-h-40 overflow-y-auto">
                      {availableCalendars.map((cal) => (
                        <div key={cal.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`cal-${cal.id}`}
                            checked={formData.calendarIds.includes(cal.id)}
                            onCheckedChange={() => toggleCalendar(cal.id)}
                            data-testid={`checkbox-calendar-${cal.id}`}
                          />
                          <label
                            htmlFor={`cal-${cal.id}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {cal.summary}
                            {cal.primary && (
                              <Badge variant="secondary" className="ml-2 text-xs">Primary</Badge>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                      data-testid="switch-talent-active"
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>
                </div>

                {/* Task #404 — Members (bands/troupes). Only available after the
                    talent exists, since members hang off a saved talentId. */}
                {editingTalent && (
                  <TalentMembersSection talentId={editingTalent.id} talentName={editingTalent.name} />
                )}

                {/* Task #611 — per-talent financial visibility gate. Default
                    is OFF so artists never see booking $ unless an admin
                    explicitly enables it. The server re-enforces this on
                    every schedule + tour response. */}
                <div className="flex items-start justify-between gap-4 border-t pt-4">
                  <div>
                    <Label htmlFor="financialsVisibleToTalent" className="text-base">
                      Show booking financials to this artist
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      When off, the artist's portal and exports never show the
                      booking dollar amount. Server-enforced — flipping this
                      takes effect on the next page load.
                    </p>
                  </div>
                  <Switch
                    id="financialsVisibleToTalent"
                    checked={formData.financialsVisibleToTalent}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, financialsVisibleToTalent: checked })
                    }
                    data-testid="switch-talent-financials-visible"
                  />
                </div>

                {/* Task #708 — per-talent calendar push field overrides.
                    Each field is a three-state select: Use workspace
                    default (null), Always on (true), Always off (false). */}
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <Label className="text-base">Calendar Event Field Overrides</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Override the workspace default for this artist. "Use default"
                      inherits the Calendar Settings selection.
                    </p>
                  </div>
                  {([
                    ["calPushTitleAccountName", "Account name in title"],
                    ["calPushTitleAddress", "Address in title"],
                    ["calPushTitleRebookPrefix", "'Rebook' prefix in title"],
                    ["calPushAddressInBody", "Address in description"],
                    ["calPushContactName", "Contact name"],
                    ["calPushContactPhone", "Contact phone"],
                    ["calPushRate", "Event rate"],
                    ["calPushReviewLink", "Post-event review link"],
                    ["calPushHoldStatus", "'HOLD:' status prefix"],
                    ["allowPostReviewClientEmail", "Allow post-review client email"],
                  ] as const).map(([key, label]) => {
                    const v = formData[key as keyof typeof formData] as boolean | null;
                    const stringVal = v === null || v === undefined ? "default" : v ? "on" : "off";
                    return (
                      <div key={key} className="flex items-center justify-between gap-3">
                        <Label htmlFor={`override-${key}`} className="text-sm flex-1">
                          {label}
                        </Label>
                        <Select
                          value={stringVal}
                          onValueChange={(val) =>
                            setFormData({
                              ...formData,
                              [key]: val === "default" ? null : val === "on",
                            })
                          }
                        >
                          <SelectTrigger
                            id={`override-${key}`}
                            className="w-[160px]"
                            data-testid={`select-override-${key}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Use default</SelectItem>
                            <SelectItem value="on">Always on</SelectItem>
                            <SelectItem value="off">Always off</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={resetForm} data-testid="button-cancel-talent">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save-talent"
                >
                  {editingTalent ? "Save Changes" : "Create Talent"}
                </Button>
              </div>
            </div>
          )}
        </div>
    </>
  );

  if (inPanel) {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
          {mainContent}
        </div>
        {childDialogs}
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        {mainContent}
      </DialogContent>
      {childDialogs}
    </Dialog>
  );
}

// ── Task #404 — Members (bands / troupes) ────────────────────────────────
// Collapsible section inside the talent edit form. Mirrors the client
// "additional contacts" pattern: inline add/edit/delete plus a per-member
// Google Calendar connect (admin OAuth or email invite). Only rendered when
// editing an existing talent, since members hang off a saved talentId.
interface TalentMemberWithStatus {
  id: string;
  talentId: string;
  name: string;
  email: string | null;
  phone: string | null;
  userId: string | null;
  accessLevel: "none" | "read" | "write";
  isPrimaryContact: boolean;
  calendarConnected: boolean;
  calendarEmail: string | null;
  calendarStatus: "connected" | "invited" | "not_invited";
}

function TalentMembersSection({ talentId, talentName }: { talentId: string; talentName: string }) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<{ name: string; email: string; phone: string; accessLevel: "none" | "read" | "write"; isPrimaryContact: boolean }>({ name: "", email: "", phone: "", accessLevel: "read", isPrimaryContact: false });
  const [deleteTarget, setDeleteTarget] = useState<TalentMemberWithStatus | null>(null);
  const [resetTarget, setResetTarget] = useState<TalentMemberWithStatus | null>(null);
  const [newPassword, setNewPassword] = useState("");

  const membersKey = ["/api/talent", talentId, "members"];
  const { data: members = [], isLoading } = useQuery<TalentMemberWithStatus[]>({
    queryKey: membersKey,
    queryFn: async () => {
      const res = await fetch(buildPath(`/api/talent/${talentId}/members`), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load members");
      return res.json();
    },
    enabled: !!talentId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: membersKey });
  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", accessLevel: "read", isPrimaryContact: false });
    setAdding(false);
    setEditingId(null);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", buildPath(`/api/talent/${talentId}/members`), {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        accessLevel: form.accessLevel,
        isPrimaryContact: form.accessLevel === "none" ? false : form.isPrimaryContact,
      });
      return res.json();
    },
    onSuccess: () => { invalidate(); resetForm(); toast({ title: "Member added" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to add member", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("PATCH", buildPath(`/api/talent/${talentId}/members/${id}`), {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        accessLevel: form.accessLevel,
        isPrimaryContact: form.accessLevel === "none" ? false : form.isPrimaryContact,
      });
      return res.json();
    },
    onSuccess: () => { invalidate(); resetForm(); toast({ title: "Member updated" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to update member", variant: "destructive" }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      const res = await apiRequest("POST", buildPath(`/api/talent/${talentId}/members/${id}/reset-password`), { newPassword: password });
      return res.json();
    },
    onSuccess: () => { setResetTarget(null); setNewPassword(""); toast({ title: "Password reset", description: "The member must set a new password on next login." }); },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to reset password", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", buildPath(`/api/talent/${talentId}/members/${id}`)); },
    onSuccess: () => { invalidate(); setDeleteTarget(null); toast({ title: "Member removed" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to remove member", variant: "destructive" }),
  });

  const inviteMutation = useMutation({
    mutationFn: async (member: TalentMemberWithStatus) => {
      const res = await apiRequest("POST", buildPath(`/api/talent/${talentId}/members/${member.id}/calendar/invite`));
      return res.json();
    },
    onSuccess: (_d, member) => { invalidate(); toast({ title: "Invite sent", description: `An invite link was emailed to ${member.name}.` }); },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to send invite", variant: "destructive" }),
  });

  const startEdit = (m: TalentMemberWithStatus) => {
    setEditingId(m.id);
    setAdding(false);
    setForm({ name: m.name, email: m.email || "", phone: m.phone || "", accessLevel: m.accessLevel || "read", isPrimaryContact: !!m.isPrimaryContact });
  };

  const statusBadge = (m: TalentMemberWithStatus) => {
    if (m.calendarStatus === "connected") {
      return <Badge variant="secondary" className="text-xs gap-1" data-testid={`badge-member-cal-${m.id}`}><CheckCircle className="w-3 h-3 text-green-500" />Calendar connected</Badge>;
    }
    if (m.calendarStatus === "invited") {
      return <Badge variant="outline" className="text-xs gap-1" data-testid={`badge-member-cal-${m.id}`}><Mail className="w-3 h-3" />Invite pending</Badge>;
    }
    return <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`badge-member-cal-${m.id}`}>No calendar</Badge>;
  };

  const accessBadge = (m: TalentMemberWithStatus) => {
    if (m.accessLevel === "write") {
      return <Badge variant="secondary" className="text-xs" data-testid={`badge-member-access-${m.id}`}>Write</Badge>;
    }
    if (m.accessLevel === "read") {
      return <Badge variant="outline" className="text-xs" data-testid={`badge-member-access-${m.id}`}>Read only</Badge>;
    }
    return <Badge variant="outline" className="text-xs text-muted-foreground" data-testid={`badge-member-access-${m.id}`}>No access</Badge>;
  };

  const formRow = (mode: "create" | "edit", id?: string) => (
    <div className="grid gap-2 border rounded-md p-3 bg-muted/30">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Input
          placeholder="Member name *"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="input-member-name"
        />
        <Input
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="input-member-email"
        />
        <Input
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="input-member-phone"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Portal access</Label>
          <Select
            value={form.accessLevel}
            onValueChange={(v) => setForm({ ...form, accessLevel: v as "none" | "read" | "write", isPrimaryContact: v === "none" ? false : form.isPrimaryContact })}
          >
            <SelectTrigger data-testid="select-member-access">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No access</SelectItem>
              <SelectItem value="read">Read only</SelectItem>
              <SelectItem value="write">Write</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-5">
          <Switch
            id="member-portal-manager"
            checked={form.isPrimaryContact}
            disabled={form.accessLevel === "none"}
            onCheckedChange={(checked) => setForm({ ...form, isPrimaryContact: checked })}
            data-testid="switch-member-portal-manager"
          />
          <Label htmlFor="member-portal-manager" className="text-sm">
            Portal Manager
            <span className="block text-xs text-muted-foreground font-normal">
              {form.accessLevel === "none" ? "Requires portal access" : "The act's primary portal contact"}
            </span>
          </Label>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={resetForm} data-testid="button-cancel-member">Cancel</Button>
        <Button
          size="sm"
          disabled={!form.name.trim() || createMutation.isPending || updateMutation.isPending}
          onClick={() => (mode === "create" ? createMutation.mutate() : updateMutation.mutate(id!))}
          data-testid="button-save-member"
        >
          {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "create" ? "Add Member" : "Save")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="border-t pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-base">Members</Label>
          <p className="text-xs text-muted-foreground mt-1">
            For bands or troupes. Each member can connect their own Google Calendar, and every booking for {talentName} is pushed to all connected members.
          </p>
        </div>
        {!adding && !editingId && (
          <Button variant="outline" size="sm" onClick={() => { setAdding(true); setForm({ name: "", email: "", phone: "", accessLevel: "read", isPrimaryContact: false }); }} data-testid="button-add-member">
            <Plus className="w-4 h-4 mr-1" />Add Member
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-2">
          <Loader2 className="w-4 h-4 animate-spin" />Loading members...
        </div>
      ) : (
        <div className="space-y-2">
          {members.length === 0 && !adding && (
            <p className="text-sm text-muted-foreground">No members yet. Add members to fan calendar bookings out to the whole group.</p>
          )}
          {members.map((m) => (
            editingId === m.id ? (
              <div key={m.id}>{formRow("edit", m.id)}</div>
            ) : (
              <div key={m.id} className="flex items-center justify-between gap-3 border rounded-md p-3" data-testid={`row-member-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm" data-testid={`text-member-name-${m.id}`}>{m.name}</span>
                    {m.isPrimaryContact && (
                      <Badge variant="default" className="text-xs gap-1" data-testid={`badge-member-manager-${m.id}`}>
                        <Star className="w-3 h-3" />Portal Manager
                      </Badge>
                    )}
                    {accessBadge(m)}
                    {statusBadge(m)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {m.calendarConnected && m.calendarEmail ? m.calendarEmail : (m.email || "No email")}
                    {m.phone ? ` · ${m.phone}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={m.email ? "Email an invite (connect calendar & create portal login)" : "Add an email to invite"}
                    disabled={!m.email || inviteMutation.isPending}
                    onClick={() => inviteMutation.mutate(m)}
                    data-testid={`button-member-invite-${m.id}`}
                  >
                    <Mail className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={m.userId ? "Reset portal password" : "Member must accept their invite before a password can be reset"}
                    disabled={!m.userId}
                    onClick={() => { setResetTarget(m); setNewPassword(""); }}
                    data-testid={`button-member-reset-password-${m.id}`}
                  >
                    <KeyRound className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => startEdit(m)} data-testid={`button-edit-member-${m.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(m)} data-testid={`button-delete-member-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          ))}
          {adding && formRow("create")}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {deleteTarget?.name} and disconnects their calendar from {talentName}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-member">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete-member"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPassword(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              Set a new portal password for {resetTarget?.name}. They'll be required to choose their own password the next time they log in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="member-new-password">New password</Label>
            <Input
              id="member-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="input-member-new-password"
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => { setResetTarget(null); setNewPassword(""); }} data-testid="button-cancel-member-reset">Cancel</Button>
            <Button
              disabled={newPassword.length < 8 || resetPasswordMutation.isPending}
              onClick={() => resetTarget && resetPasswordMutation.mutate({ id: resetTarget.id, password: newPassword })}
              data-testid="button-confirm-member-reset"
            >
              {resetPasswordMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
